import React from 'react';
import {
  Document,
  Font,
  Image,
  Page,
  PDFViewer,
  Text,
  View,
} from '@react-pdf/renderer';
import { TimesheetStatusDisplayValue } from 'constants/statusValues';
import { BuPath } from 'constants/buPath';
import { getLocalDateTime } from 'utils/utils';
import moment from 'moment';
import { IsAllowedACL } from 'utils/aclHelper';
import { ModuleACL } from 'enums/entitlements.ts';
import { SERVICE_TYPES } from 'enums/serviceTypes.ts';
import styles from './styles';
import AEPLogo from 'assets/images/LoginAEP_Logo.png';

Font.registerHyphenationCallback(w => [w]);

const TimesheetPDF = ({
  data: {
    timesheet,
    purchaseOrder,
    user,
    getApproverMenuOptions,
    lookup,
    serviceTypeOptions,
  },
}) => {
  const isDistribution = BuPath.DIST.includes(purchaseOrder?.buPath);
  const isGeneration = BuPath.GEN.includes(purchaseOrder.buPath);
  const isTransmission = BuPath.TRANS === purchaseOrder?.buPath;

  const datePrinted = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const sortedHistories = timesheet?.timesheetStatushistories
    ?.slice()
    .sort((a, b) => new Date(a.updatedDate) - new Date(b.updatedDate));

  const statusHistoryList =
    sortedHistories?.length > 0 ? sortedHistories : [{}];

  const approver = getApproverMenuOptions()?.find(
    obj => obj.value === timesheet?.timesheetApprovals[0]?.approver,
  );
  const opco = lookup?.operatingCompany?.find(
    obj => parseInt(obj.ocNmbr) === parseInt(timesheet.ocNmbr),
  );

  const materialStatus = lookup?.materialStatus?.find(
    obj =>
      parseInt(obj.materialStatusNmbr) === parseInt(timesheet.materialStatusId),
  );

  const workArea = lookup?.workArea?.find(
    obj => parseInt(obj?.workAreaId) === parseInt(timesheet.workAreaId),
  );

  const isCUTimesheet = () => {
    let filteredList = timesheet?.classificationList.filter(obj =>
      obj.name.includes('timesheetCus'),
    );

    if (filteredList.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const renderWorkOrderCells = activity => {
    const timesheetDistributedLines = getAllDistibutedLines(
      activity.activitynum,
    );

    const totalPercentage = timesheetDistributedLines.reduce((sum, item) => {
      const percentage = parseFloat(item.percentage);
      return sum + (isNaN(percentage) ? 0 : percentage);
    }, 0);

    const totalAmount = timesheetDistributedLines.reduce((sum, item) => {
      const amount = parseFloat(item.lineAmount);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    return timesheetDistributedLines.map((accounts, idx) => (
      <View key={`dist-key-${idx}`}>
        <View style={styles.tableRow}>
          <Text style={[styles.tableCell, styles.tableFirstCell]}>
            {accounts.workorder}
          </Text>
          <Text style={styles.tableCell}>{accounts.glbu ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.dept ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.accountNmbr ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.costComponent ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.abmsActivity ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.abmsProject ?? ''}</Text>
          <Text style={styles.tableCell}>
            {accounts.projectBusinessUnit ?? ''}
          </Text>
          <Text style={styles.tableCell}>{accounts.percentage ?? ''}</Text>
          <Text style={styles.tableCell}>{accounts.lineAmount ?? ''}</Text>
        </View>
        {timesheetDistributedLines.length === idx + 1 && (
          <View style={styles.tableRow}>
            <Text style={[styles.workOrderBottomCell, styles.tableFirstCell]}>
              Total Percentage (%)
            </Text>
            <Text style={styles.tableCell}>{totalPercentage.toFixed(2)}</Text>
            <Text style={styles.workOrderBottomCell}>Total Amount ($)</Text>
            <Text style={styles.tableCell}>{totalAmount.toFixed(2)}</Text>
          </View>
        )}
      </View>
    ));
  };

  const getAllDistibutedLines = activityNum => {
    const result = timesheet?.timesheetAccounts?.filter(
      item => item.activitynum === activityNum,
    );
    return result.length > 0 ? result : [{}];
  };

  const getApproverName = approverId => {
    const { label } =
      getApproverMenuOptions().find(
        obj => obj.value?.toLowerCase() === approverId?.toLowerCase(),
      ) ?? {};
    return label ?? ' ';
  };

  const getTotalSumActivity = (timesheetActivities = []) => {
    return (
      timesheetActivities
        .reduce((acc, curr) => (acc += curr.lineAmount), 0)
        .toFixed(2) ?? 0
    );
  };

  const getServiceType = classificationObj => {
    return (
      serviceTypeOptions.find(
        srvcTypeOpts => srvcTypeOpts.value === classificationObj.name,
      )?.label || ''
    );
  };

  const getLaborShiftOptions = fieldVal => {
    const result = lookup?.shiftOptions.find(
      obj => obj.value + '' === fieldVal + '',
    );
    return result?.label ?? '';
  };

  const getActivityNum = (fieldVal, activityList) => {
    return activityList.find(o => o?.value === fieldVal)?.label ?? '';
  };

  const getClassificationCellValues = (classificationObj, item) => {
    if (classificationObj.name === SERVICE_TYPES.LABOR) {
      if (item.name === 'shift') return getLaborShiftOptions(item.value);
    }

    if (item.name === 'activitynum') {
      return getActivityNum(item.value, item.values);
    }
    return item.value;
  };

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.borderFrame}>
          {/* Timesheet Info Start */}
          <View style={[styles.section, styles.row]}>
            <Image src={AEPLogo} style={{ height: '10vh' }} />

            <View style={styles.infoMainHeaderView}>
              <View>
                <Text style={styles.infoMainHeaderLabel}>
                  CAA Timesheet Record
                </Text>
                <Text style={styles.value}>{timesheet?.recordNmbr ?? ''}</Text>
              </View>
              <View>
                <Text style={styles.infoMainHeaderLabel}>Vendor Name</Text>
                <Text style={styles.value}>
                  {timesheet?.vendorVendor?.vendorName}
                </Text>
              </View>
            </View>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCellHeader, styles.tableFirstCell]}>
              Contract No
            </Text>
            <Text style={styles.tableCellHeader}>PO</Text>
            <Text style={styles.tableCellHeader}>Status</Text>
            {timesheet?.timesheetActivitySummaries?.length > 0 && (
              <Text style={styles.tableCellHeader}>Grand Total ($)</Text>
            )}
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.tableFirstCell]}>
              {purchaseOrder?.contractRefnum}
            </Text>
            <Text style={styles.tableCell}>{purchaseOrder?.ponum ?? ''}</Text>
            <Text style={styles.tableCell}>
              {TimesheetStatusDisplayValue[timesheet?.caastatus]}
            </Text>
            {timesheet?.timesheetActivitySummaries?.length > 0 && (
              <Text style={styles.tableCell}>
                {getTotalSumActivity(timesheet?.timesheetActivitySummaries)}
              </Text>
            )}
          </View>
          {/* Timesheet Info End */}

          <View style={styles.divider} />

          {/* Timesheet Header Start */}
          <View style={styles.tableRow}>
            {(isTransmission || isGeneration) && (
              <Text style={[styles.tableCellHeader, styles.tableFirstCell]}>
                Timesheet Start Date
              </Text>
            )}
            {(isTransmission || isGeneration) && (
              <Text style={styles.tableCellHeader}>Timesheet End Date: </Text>
            )}
            {isDistribution && (
              <Text style={[styles.tableCellHeader, styles.tableFirstCell]}>
                Week End Date
              </Text>
            )}

            <Text
              style={[
                styles.tableCellHeader,
                // @ts-ignore
                !isTransmission &&
                  !isGeneration &&
                  !isDistribution && { borderLeft: '1pt solid #333' },
              ]}
            >
              Vendor Ref Number
            </Text>
            <Text style={styles.tableCellHeader}>Vendor Timesheet</Text>
            <Text style={styles.tableCellHeader}>UVL Estimate</Text>
            {((isTransmission && purchaseOrder?.reqtsappr) ||
              isDistribution) && (
              <Text style={styles.tableCellHeader}>Approver</Text>
            )}
          </View>
          <View style={styles.tableRow}>
            {(isTransmission || isGeneration) && (
              <Text style={[styles.tableCell, styles.tableFirstCell]}>
                {moment(timesheet?.workStartDate).format('MM/DD/YYYY') ?? ''}
              </Text>
            )}
            {(isTransmission || isGeneration) && (
              <Text style={styles.tableCell}>
                {moment(timesheet?.workEndDate).format('MM/DD/YYYY') ?? ''}
              </Text>
            )}
            {isDistribution && (
              <Text style={[styles.tableCell, styles.tableFirstCell]}>
                {moment(timesheet?.weekEndDate).format('MM/DD/YYYY') ?? ''}
              </Text>
            )}

            <Text
              style={[
                styles.tableCell,
                // @ts-ignore
                !isTransmission &&
                  !isGeneration &&
                  !isDistribution && { borderLeft: '1pt solid #333' },
              ]}
            >
              {timesheet?.contRefNumeric || ''}
            </Text>
            <Text style={styles.tableCell}>
              {timesheet?.vendorTimesheet || ''}
            </Text>
            <Text style={styles.tableCell}>
              {timesheet?.uvl ? 'Yes' : 'No'}
            </Text>
            {((isTransmission && purchaseOrder?.reqtsappr) ||
              isDistribution) && (
              <Text style={styles.tableCell}>
                {approver?.label ? approver?.label : ''}
              </Text>
            )}
          </View>
          {/* Timesheet Header End */}

          <View style={styles.divider} />

          {/* Distribution Accounting Header Start */}
          {isDistribution && (
            <View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCellHeader, styles.tableFirstCell]}>
                  OPCO / State
                </Text>
                <Text style={styles.tableCellHeader}>Work Area</Text>
                <Text style={styles.tableCellHeader}>Work Type</Text>
                <Text style={styles.tableCellHeader}>Crew Types</Text>
                <Text style={styles.tableCellHeader}>Crew #</Text>
                <Text style={styles.tableCellHeader}>Master Project</Text>
              </View>

              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.tableFirstCell]}>
                  {opco ? `${opco?.companyAbbr} / ${opco?.stateCode}` : ''}
                </Text>
                <Text style={styles.tableCell}>
                  {workArea?.workAreaShortDesc ?? ''}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.worktype ?? ''}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.crewType ?? ''}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.crewNmbr ?? ''}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.masterProjectNumber}
                </Text>
              </View>

              <View style={styles.divider} />

              <View style={styles.tableRow}>
                <Text style={[styles.tableCellHeader, styles.tableFirstCell]}>
                  Auth Type
                </Text>
                <Text style={styles.tableCellHeader}>Authorization</Text>
                <Text style={styles.tableCellHeader}>Circuit Number</Text>
                <Text style={styles.tableCellHeader}>Sub Circuit Number</Text>
                <Text style={styles.tableCellHeader}>Miles Completed</Text>
                <Text style={styles.tableCellHeader}>Material Status</Text>
                {isCUTimesheet() && (
                  <Text style={styles.tableCellHeader}>Work Request</Text>
                )}
                {isCUTimesheet() && (
                  <Text style={styles.tableCellHeader}>Job City</Text>
                )}
                {isCUTimesheet() && (
                  <Text style={styles.tableCellHeader}>Job Description</Text>
                )}
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.tableFirstCell]}>
                  {timesheet?.authType}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.jobAuthorization?.authorizationnum}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.circuitnum ?? ''}
                </Text>
                <Text style={styles.tableCell}>
                  {timesheet?.subcircuit ?? ''}
                </Text>
                <Text style={styles.tableCell}>{timesheet?.milescomplete}</Text>
                <Text style={styles.tableCell}>
                  {materialStatus
                    ? `${materialStatus?.statusDiscription}-${materialStatus?.costComponent}`
                    : ''}
                </Text>
                {isCUTimesheet() && (
                  <Text style={styles.tableCell}>{timesheet.wr ?? ''}</Text>
                )}
                {isCUTimesheet() && (
                  <Text style={styles.tableCell}>
                    {timesheet.jobCity ?? ''}
                  </Text>
                )}
                {isCUTimesheet() && (
                  <Text style={styles.tableCell}>
                    {timesheet.jobDescription ?? ''}
                  </Text>
                )}
              </View>
              <View style={styles.divider} />
            </View>
          )}
          {/* Distribution Accounting Header End */}

          {/* Timesheet Activity Summary Start */}
          {timesheet?.timesheetActivitySummaries?.length > 0 &&
            timesheet?.timesheetActivitySummaries?.map(
              (activity, activityIdx, activityArr) => (
                <View
                  style={styles.activitySetion}
                  key={`activity-${activityIdx}`}
                >
                  <View style={styles.activityRow} break>
                    <View style={[styles.activityInfo, { width: '8%' }]}>
                      <Text style={styles.activityLabel}>POline: </Text>
                      <Text style={styles.activityValue}>
                        {activity.polinenum ?? ''}
                      </Text>
                    </View>
                    <View style={[styles.activityInfo, { width: '25%' }]}>
                      <Text style={styles.activityLabel}>
                        Activity / SOV No:{'\u00A0'}
                      </Text>
                      <Text style={styles.activityValue}>
                        {activity.activitynum ?? ''}
                      </Text>
                    </View>
                    <View style={[styles.activityInfo, { width: '57%' }]}>
                      <Text style={styles.activityLabel}>Description: </Text>
                      <Text style={styles.activityValue}>
                        {activity.description ?? ''}
                      </Text>
                    </View>
                    <View style={[styles.activityInfo, { width: '10%' }]}>
                      <Text style={styles.activityLabel}>Amount ($): </Text>
                      <Text style={styles.activityValue}>
                        {Number(activity.lineAmount).toFixed(2)}
                      </Text>
                    </View>
                  </View>
                  <View style={styles.tableRow}>
                    <Text
                      style={[styles.activityCellHeader, styles.tableFirstCell]}
                    >
                      Work Order
                    </Text>
                    <Text style={styles.activityCellHeader}>GLBU</Text>
                    <Text style={styles.activityCellHeader}>Department</Text>
                    <Text style={styles.activityCellHeader}>
                      Account Number
                    </Text>
                    <Text style={styles.activityCellHeader}>
                      Cost Component
                    </Text>
                    <Text style={styles.activityCellHeader}>ABMS Activity</Text>
                    <Text style={styles.activityCellHeader}>Project ID</Text>
                    <Text style={styles.activityCellHeader}>PCBU</Text>
                    <Text style={styles.activityCellHeader}>Per(%)</Text>
                    <Text style={styles.activityCellHeader}>Amount ($)</Text>
                  </View>
                  {renderWorkOrderCells(activity)}

                  {activityArr.length === activityIdx + 1 && (
                    <View style={styles.divider} />
                  )}
                </View>
              ),
            )}
          {/* Timesheet Activity Summary End */}

          {/* Timesheet Approver Field Start */}
          {isGeneration &&
            (IsAllowedACL(user, ModuleACL.TIMESHEET, 'can_approve_timesheet') ||
              IsAllowedACL(
                user,
                ModuleACL.TIMESHEET,
                'can_review_costcord_timesheet',
              )) && (
              <View>
                <Text style={styles.sectionLabel} break>
                  Cost Activities Approver
                </Text>
                <View style={styles.approverContainer} break>
                  {timesheet?.timesheetApprovals?.map((tSheetApprover, idx) => (
                    <View
                      key={`cost-approver-${idx}`}
                      style={styles.approverCell}
                    >
                      <Text style={styles.label}>Approver {idx + 1}:</Text>
                      <Text style={styles.value}>
                        {getApproverName(tSheetApprover?.approver)}
                      </Text>
                    </View>
                  ))}
                </View>
                <View style={styles.divider} />
              </View>
            )}
          {/* Timesheet Approver Field End */}

          {/* Timesheet Classification Start */}
          {timesheet?.classificationList?.length ? (
            timesheet?.classificationList?.map((classificationObj, idx) => {
              return (
                <View key={`classificationList-${idx}`}>
                  {classificationObj?.values?.map((record, recordIdx) => (
                    <View key={`srvc-record-${recordIdx}`} wrap={false}>
                      {recordIdx === 0 && (
                        <View style={styles.classificationHeader}>
                          <Text style={styles.sectionLabel}>
                            Service Type:{' '}
                          </Text>
                          <Text style={styles.sectionLabel}>
                            {getServiceType(classificationObj)}
                          </Text>
                        </View>
                      )}
                      <View style={styles.classificationRow} break>
                        {record.map((item, itemIdx) => (
                          <View
                            key={`srvc-item-${itemIdx}`}
                            style={styles.classificationCell}
                          >
                            <Text style={styles.label}>{item.label}:</Text>
                            <Text style={styles.value}>
                              {getClassificationCellValues(
                                classificationObj,
                                item,
                              ) ?? ' '}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                  <View style={styles.divider} />
                </View>
              );
            })
          ) : (
            <View style={styles.divider} />
          )}
          {/* Timesheet Classification End */}

          {/* Timesheet Status History Start */}
          <View>
            {statusHistoryList?.map((obj, idx) => (
              <View wrap={false}>
                {idx === 0 && (
                  <View>
                    <Text style={styles.sectionLabel}>
                      Timesheet Status History and Comments
                    </Text>
                    <View style={styles.tableRow}>
                      <Text
                        style={[styles.tableCellHeader, styles.tableFirstCell]}
                      >
                        Updated Date
                      </Text>
                      <Text style={styles.tableCellHeader}>Status</Text>
                      <Text style={styles.tableCellHeader}>Comments</Text>
                      <Text style={styles.tableCellHeader}>Updated By</Text>
                    </View>
                  </View>
                )}

                <View style={styles.tableRow} key={`status-history-${idx}`}>
                  <Text style={[styles.tableCell, styles.tableFirstCell]} break>
                    {getLocalDateTime(obj.updatedDate) ?? ''}
                  </Text>
                  <Text style={styles.tableCell} break>
                    {TimesheetStatusDisplayValue[obj.caastatus] ?? ''}
                  </Text>
                  <Text style={styles.tableCell} break>
                    {obj.comments ?? ''}
                  </Text>
                  <Text style={styles.tableCell} break>
                    {obj.updatedByName ?? ''}
                  </Text>
                </View>
              </View>
            ))}
          </View>
          {/* Timesheet Status History End */}

          <View style={styles.divider} />

          {/* Comments Section Start */}
          <View wrap={false}>
            <Text style={styles.sectionLabel}>Timesheet Comments</Text>
            <View style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellTop,
                  styles.tableFirstCell,
                ]}
              >
                {timesheet?.comments || ''}
              </Text>
            </View>
          </View>
          {/* Comments Section End */}

          <View style={styles.divider} />

          {/* Attachments Start */}
          <View break>
            <Text style={styles.sectionLabel}>Attachments</Text>
            {timesheet?.timesheetAttachments?.length === 0 ? (
              <Text style={styles.value}>(No attachments provided)</Text>
            ) : (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {timesheet?.timesheetAttachments?.map((item, index) => (
                  <View
                    style={[styles.tableRow, styles.attachmentsView]}
                    key={`attachments-${index}`}
                  >
                    <Text style={[styles.label, { marginRight: 4 }]}>•</Text>
                    <Text style={styles.value}>{item.fileName ?? ''}</Text>
                  </View>
                ))}
              </View>
            )}

            <View style={styles.divider} />
          </View>
          {/* Attachments End */}

          <View break>
            <Text style={styles.section}>
              <Text style={styles.label}>Entered By:</Text>
              <Text style={styles.value}>{timesheet?.enteredBy ?? ''}</Text>
              <Text style={styles.label}> | </Text>
              <Text style={styles.label}>Updated By:</Text>
              <Text style={styles.value}>{timesheet?.updatedBy ?? ''}</Text>
            </Text>
            <Text>
              <Text style={styles.label}>Entered Date:</Text>
              <Text style={styles.value}>
                {moment(timesheet?.enteredDate).format('MM/DD/YYYY') ?? ''}
              </Text>
              <Text style={styles.label}> | </Text>
              <Text style={styles.label}>Updated Date:</Text>
              <Text style={styles.value}>
                {moment(timesheet?.updatedDate).format('MM/DD/YYYY') ?? ''}
              </Text>
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={{ flex: 1, textAlign: 'left' }}>
            <Text style={styles.label}>Printed on: </Text>
            <Text style={styles.value}>{datePrinted}</Text>
          </Text>
          <Text style={{ flex: 1, textAlign: 'center' }}>
            <Text style={styles.label}>CAA Timesheet Record: </Text>
            <Text style={styles.value}>{timesheet?.recordNmbr ?? ''}</Text>
          </Text>
          <Text
            style={{ flex: 1, textAlign: 'right' }}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
            fixed
          />
        </View>
      </Page>
    </Document>
  );
};

export default TimesheetPDF;
